import type { NotificationRecord, UserType } from "@shared/types";
import { create } from "zustand";
import {
	getNotifications,
	markAllNotificationsRead,
	markNotificationRead,
} from "../services/notifications-api.service";

let initialLoadPromise: Promise<void> | null = null;
let loadGeneration = 0;

function sortByNewest(a: NotificationRecord, b: NotificationRecord): number {
	return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
}

export function mergeNotifications(
	current: NotificationRecord[],
	incoming: NotificationRecord[],
): NotificationRecord[] {
	const byId = new Map<string, NotificationRecord>();

	for (const notification of [...current, ...incoming]) {
		byId.set(notification.id, notification);
	}

	return [...byId.values()].sort(sortByNewest);
}

function getLatestCreatedAt(
	notifications: NotificationRecord[],
): string | null {
	return notifications[0]?.createdAt ?? null;
}

/**
 * Settled realtime state, as distinct from `isConnecting`.
 *
 * `isConnecting` is in-flight only, and both the success and the failure
 * callback cleared it -- so nothing could tell "connected" from "gave up".
 * Realtime is off by default (ABLY_ENABLED=false), and every surface degraded
 * silently, which is what made the absence invisible.
 */
export type RealtimeStatus = "connecting" | "live" | "off";

/** Unread notifications for roles other than the active one, by role. */
export type OtherRoleUnread = Partial<Record<UserType, number>>;

interface NotificationsState {
	notifications: NotificationRecord[];
	lastSeenCreatedAt: string | null;
	isLoaded: boolean;
	isConnecting: boolean;
	realtimeStatus: RealtimeStatus;
	otherRoleUnread: OtherRoleUnread;
	loadInitial: () => Promise<void>;
	/**
	 * Add a realtime notification to the list when it is for `activeRole`.
	 * One for another role is left out of the list and, when unread, adds one
	 * to that role's `otherRoleUnread` count. A null `activeRole` adds it.
	 */
	appendRealtime: (
		notification: NotificationRecord,
		activeRole: UserType | null,
	) => void;
	/** Replace the per-role counts, e.g. with what GET /users/me/roles returns. */
	setOtherRoleUnread: (counts: OtherRoleUnread) => void;
	catchUp: (after?: string | null) => Promise<void>;
	markRead: (id: string) => Promise<boolean>;
	markAllRead: () => Promise<boolean>;
	setConnecting: (connecting: boolean) => void;
	setRealtimeStatus: (status: RealtimeStatus) => void;
	reset: () => void;
}

const initialState = {
	notifications: [],
	lastSeenCreatedAt: null,
	isLoaded: false,
	isConnecting: false,
	realtimeStatus: "off" as RealtimeStatus,
	otherRoleUnread: {} as OtherRoleUnread,
};

export const useNotificationsStore = create<NotificationsState>((set, get) => ({
	...initialState,

	loadInitial: () => {
		if (get().isLoaded) {
			return Promise.resolve();
		}

		if (initialLoadPromise) {
			return initialLoadPromise;
		}

		const generation = loadGeneration;
		const pendingLoad = (async () => {
			const notifications = await getNotifications({ limit: 50 });
			if (generation !== loadGeneration) {
				return;
			}

			set({
				notifications,
				lastSeenCreatedAt: getLatestCreatedAt(notifications),
				isLoaded: true,
			});
		})().finally(() => {
			if (initialLoadPromise === pendingLoad) {
				initialLoadPromise = null;
			}
		});

		initialLoadPromise = pendingLoad;
		return pendingLoad;
	},

	appendRealtime: (notification, activeRole) => {
		if (activeRole && notification.audienceRole !== activeRole) {
			if (!notification.isRead) {
				const role = notification.audienceRole;
				set((state) => ({
					otherRoleUnread: {
						...state.otherRoleUnread,
						[role]: (state.otherRoleUnread[role] ?? 0) + 1,
					},
				}));
			}
			return;
		}

		const notifications = mergeNotifications(get().notifications, [
			notification,
		]);
		set({
			notifications,
			lastSeenCreatedAt: getLatestCreatedAt(notifications),
		});
	},

	catchUp: async (after) => {
		const afterCursor = after ?? get().lastSeenCreatedAt;
		if (!afterCursor) return;

		const generation = loadGeneration;
		const incoming = await getNotifications({ after: afterCursor, limit: 100 });
		if (generation !== loadGeneration || incoming.length === 0) return;

		const notifications = mergeNotifications(get().notifications, incoming);
		set({
			notifications,
			lastSeenCreatedAt: getLatestCreatedAt(notifications),
		});
	},

	markRead: async (id) => {
		const result = await markNotificationRead(id);
		if (!result.success) return false;

		set((state) => ({
			notifications: state.notifications.map((notification) =>
				notification.id === id
					? { ...notification, isRead: true }
					: notification,
			),
		}));
		return true;
	},

	markAllRead: async () => {
		const result = await markAllNotificationsRead();
		if (!result.success) return false;

		set((state) => ({
			notifications: state.notifications.map((notification) => ({
				...notification,
				isRead: true,
			})),
		}));
		return true;
	},

	setOtherRoleUnread: (counts) => set({ otherRoleUnread: counts }),

	setConnecting: (connecting) => set({ isConnecting: connecting }),

	setRealtimeStatus: (status) => set({ realtimeStatus: status }),

	reset: () => {
		loadGeneration += 1;
		initialLoadPromise = null;
		set(initialState);
	},
}));
